import {
  Button,
  Calendar,
  cn,
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@devhop/ui";
import { format, parse } from "date-fns";
import { CalendarIcon } from "lucide-react";
import { useRef, useState } from "react";

type DatePickerFieldProps = {
  /** ຄ່າ ISO ວັນທີ `yyyy-MM-dd` (ຫຼື empty) */
  value?: string;
  onChange: (value: string) => void;
  placeholder?: string;
  id?: string;
  disabled?: boolean;
  className?: string;
  /** ຈຳນວນປີໃນອະນາຄົດທີ່ dropdown ປີ ເລືອກໄດ້ (default 2) */
  yearsAhead?: number;
  /** ຈຳນວນປີໃນອະດີດທີ່ dropdown ປີ ເລືອກໄດ້ (default 5) */
  yearsBehind?: number;
};

/**
 * Date picker ສວຍ (Popover + Calendar) ແທນ native `<input type="date">`.
 * ຮັບ/ສົ່ງຄ່າເປັນ string `yyyy-MM-dd` ໃຫ້ເຂົ້າກັບ form ເກົ່າໄດ້ເລີຍ.
 * ປະຕິທິນຈະກວ້າງເທົ່າກ່ອງປຸ່ມ (ວັດເປັນ px ຕອນເປີດ).
 */
export function DatePickerField({
  value,
  onChange,
  placeholder = "ເລືອກວັນທີ",
  id,
  disabled,
  className,
  yearsAhead = 2,
  yearsBehind = 5,
}: DatePickerFieldProps) {
  const [open, setOpen] = useState(false);
  const [triggerWidth, setTriggerWidth] = useState<number>();
  const triggerRef = useRef<HTMLButtonElement>(null);

  const parsed = value ? parse(value, "yyyy-MM-dd", new Date()) : undefined;
  const selected =
    parsed && !Number.isNaN(parsed.getTime()) ? parsed : undefined;

  // ຊ່ວງປີໃນ dropdown: ອະດີດ yearsBehind ປີ → ອະນາຄົດ yearsAhead ປີ
  const currentYear = new Date().getFullYear();
  const startMonth = new Date(currentYear - yearsBehind, 0, 1);
  const endMonth = new Date(currentYear + yearsAhead, 11, 31);

  return (
    <Popover
      open={open}
      onOpenChange={(o) => {
        // ວັດຄວາມກວ້າງກ່ອງຕອນເປີດ ໃຫ້ປະຕິທິນກວ້າງເທົ່າກັນ
        if (o && triggerRef.current) {
          setTriggerWidth(triggerRef.current.offsetWidth);
        }
        setOpen(o);
      }}
    >
      <PopoverTrigger asChild>
        <Button
          ref={triggerRef}
          id={id}
          type="button"
          variant="outline"
          disabled={disabled}
          className={cn(
            "h-9 w-full justify-start gap-2 font-normal",
            !selected && "text-muted-foreground",
            className,
          )}
        >
          <CalendarIcon className="size-4 shrink-0 opacity-70" />
          {selected ? format(selected, "dd/MM/yyyy") : placeholder}
        </Button>
      </PopoverTrigger>
      <PopoverContent
        className="w-auto p-0"
        align="start"
        /* ກວ້າງຢ່າງໜ້ອຍ = ກ່ອງ trigger ແຕ່ບໍ່ບີບປະຕິທິນໃຫ້ແຄບ (min ຕາມ Calendar) */
        style={triggerWidth ? { minWidth: triggerWidth } : undefined}
      >
        <Calendar
          mode="single"
          selected={selected}
          defaultMonth={selected}
          startMonth={startMonth}
          endMonth={endMonth}
          captionLayout="dropdown"
          autoFocus
          className="w-full"
          onSelect={(d) => {
            if (d) {
              onChange(format(d, "yyyy-MM-dd"));
              setOpen(false);
            }
          }}
        />
      </PopoverContent>
    </Popover>
  );
}
